/**
 * BOGOS Bundle Builder - Gift Form
 * Adds gift information fields (From, To, Message) to the bundle builder.
 *
 * - The form (and the "Add bundle to cart" button) stay hidden until the
 *   shopper is on the LAST step of the builder, however many steps it has.
 * - When a product is chosen on the last step, the page slides down to the
 *   form so the shopper can fill it in.
 * - The add-to-cart button only shows once the required fields are filled.
 */

(function() {
  'use strict';

  // ============ CONFIGURATION ============
  const CONFIG = {
    formTitle: 'Gift Information',
    fromLabel: 'From',
    toLabel: 'To',
    messageLabel: 'Message (optional)',
    fromPlaceholder: 'Your name',
    toPlaceholder: "Recipient's name",
    messagePlaceholder: 'Write a message...',
    requireMessage: false,
    // Keep the form visible once it has been shown, even if the shopper
    // goes back to an earlier step. false = hide it again on earlier steps.
    stayVisibleAfterReveal: false,
    // Also slide down to the form the moment the last step opens (before an
    // item is picked). false = only slide after an item is chosen there.
    scrollOnReveal: false,
    // Phones: the app folds "Your bundle" (with this form and the add button)
    // into a bar at the bottom. Open it when an item is picked on the last step.
    openPanelOnPhone: true
  };
  // =======================================

  let formInjected = false;
  let revealed = false;
  let formEl, btnEl, fromInput, toInput, msgInput;

  function init() {
    // A link can switch the gift form off: ...&gift=off
    // The form is then never injected and the add button behaves as the app ships it.
    try {
      if (new URLSearchParams(window.location.search).get('gift') === 'off') return;
    } catch (e) { /* ignore */ }

    let attempts = 0;
    const maxAttempts = 60;

    const checkInterval = setInterval(() => {
      attempts++;

      const btn = document.querySelector('.bogos-bp-button-add');
      const footer = document.querySelector('.bogos-bp-widget-footer');

      if (btn && footer && !formInjected) {
        clearInterval(checkInterval);
        injectGiftForm(btn, footer);
        watchSteps();
      }

      if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
      }
    }, 500);
  }

  // ============================================
  // LAST STEP DETECTION
  // ============================================
  function getStepItems() {
    // Step nav in the widget header. BOGOS marks the current one with
    // class "active" and aria-current="step".
    let steps = document.querySelectorAll('.bogos-bp-steps-container .bogos-bp-step-item');
    if (!steps.length) steps = document.querySelectorAll('.bogos-bp-step-item');
    return steps;
  }

  function isOnLastStep() {
    const steps = getStepItems();
    if (!steps.length) return false;
    const last = steps[steps.length - 1];
    return last.classList.contains('active')
        || last.classList.contains('bogos-bp-step-active')
        || last.getAttribute('aria-current') === 'step';
  }

  // ============================================
  // WATCH FOR STEP CHANGES AND LAST-STEP PICKS
  // ============================================
  function watchSteps() {
    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        updateVisibility();
      });
    };

    // BOGOS toggles classes on the step items and sometimes re-renders the nav.
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'aria-current']
    });

    document.body.addEventListener('click', function(e) {
      // Step clicks, next/back arrows and product picks can all change the step.
      setTimeout(updateVisibility, 200);

      // A product picked while on the last step: slide down to the form.
      const pick = e.target.closest('.bogos-bp-btn-add-product, .bogos-bp-product-card');
      if (pick && isOnLastStep()) {
        setTimeout(() => {
          updateVisibility();
          // Give the panel time to slide open before scrolling inside it.
          if (openPanelOnPhone()) setTimeout(scrollToForm, 400);
          else scrollToForm();
        }, 300);
      }
    }, true);

    updateVisibility();
  }

  // ============================================
  // INJECT GIFT FORM
  // ============================================
  function injectGiftForm(btn, footer) {
    if (formInjected || document.getElementById('bogos-gift-form-wrapper')) return;
    formInjected = true;
    btnEl = btn;

    btn.style.display = 'none';

    const styles = document.createElement('style');
    styles.textContent = `
      #bogos-gift-form-wrapper {
        padding: 20px;
        margin-bottom: 15px;
        border: 1px solid #e5e5e5;
        border-radius: 8px;
        background: #fafafa;
      }
      #bogos-gift-form-wrapper[hidden] { display: none; }
      #bogos-gift-form-wrapper .gift-title {
        font-weight: 600;
        font-size: 16px;
        margin-bottom: 15px;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      #bogos-gift-form-wrapper .gift-field {
        margin-bottom: 12px;
      }
      #bogos-gift-form-wrapper .gift-field label {
        display: block;
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 5px;
        color: #333;
      }
      #bogos-gift-form-wrapper .gift-field input,
      #bogos-gift-form-wrapper .gift-field textarea {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
        box-sizing: border-box;
        font-family: inherit;
        transition: border-color 0.2s;
      }
      #bogos-gift-form-wrapper .gift-field input:focus,
      #bogos-gift-form-wrapper .gift-field textarea:focus {
        outline: none;
        border-color: #333;
      }
      #bogos-gift-form-wrapper .gift-field textarea {
        min-height: 70px;
        resize: vertical;
      }
      #bogos-gift-form-wrapper .required { color: #e53935; }
      /* Phones: the open "Your bundle" panel has a fixed height. Let the whole
         panel scroll, so the form and the add button are never cut off. */
      @media (max-width: 599px) {
        .bogos-bp-widget.active {
          overflow-y: auto !important;
          -webkit-overflow-scrolling: touch;
        }
        .bogos-bp-widget.active > * { flex-shrink: 0 !important; }
        .bogos-bp-widget.active .bogos-bp-widget-body { max-height: 40vh; }
      }
    `;
    document.head.appendChild(styles);

    const form = document.createElement('div');
    form.id = 'bogos-gift-form-wrapper';
    form.hidden = true; // stays hidden until the last step is reached
    form.innerHTML = `
      <div class="gift-title">🎁 ${CONFIG.formTitle}</div>
      <div class="gift-field">
        <label>${CONFIG.fromLabel} <span class="required">*</span></label>
        <input type="text" id="bogos-gift-from" maxlength="100" placeholder="${CONFIG.fromPlaceholder}">
      </div>
      <div class="gift-field">
        <label>${CONFIG.toLabel} <span class="required">*</span></label>
        <input type="text" id="bogos-gift-to" maxlength="100" placeholder="${CONFIG.toPlaceholder}">
      </div>
      <div class="gift-field">
        <label>${CONFIG.messageLabel}${CONFIG.requireMessage ? ' <span class="required">*</span>' : ''}</label>
        <textarea id="bogos-gift-message" maxlength="500" placeholder="${CONFIG.messagePlaceholder}"></textarea>
      </div>
    `;

    footer.insertBefore(form, btn);
    formEl = form;

    fromInput = document.getElementById('bogos-gift-from');
    toInput = document.getElementById('bogos-gift-to');
    msgInput = document.getElementById('bogos-gift-message');

    fromInput.addEventListener('input', validate);
    toInput.addEventListener('input', validate);
    msgInput.addEventListener('input', validate);

    // Listen on window in the capture phase: the theme's Bundle Inventory Fix
    // (theme.liquid) catches this button's click on document and stops it, and
    // the app can redraw the button, so a listener on the button can miss it.
    if (window.__bogosGiftSaveBound) return;
    window.__bogosGiftSaveBound = true;
    window.addEventListener('click', function(e) {
      if (!e.target || !e.target.closest || !e.target.closest('.bogos-bp-button-add')) return;
      if (!fromInput || !document.body.contains(fromInput)) return;
      // Each box keeps its own message: remember it now, and once the app has
      // put the box in the cart, write it onto that box's line (see
      // assignPendingGift). The app can leave for the cart page within a
      // second, so the write happens on whichever page loads next as well.
      const m = /\/bundle\/(\d+)/.exec(window.location.pathname);
      setPendingGift({
        from: fromInput.value.trim().slice(0, 100),
        to: toInput.value.trim().slice(0, 100),
        message: msgInput.value.trim().slice(0, 500),
        offer: m ? m[1] : '',
        t: Date.now()
      });
      [2500, 6000, 12000].forEach(function(ms) { setTimeout(assignPendingGift, ms); });
    }, true);
  }

  // ============================================
  // GIFT MESSAGE PER BOX
  // ============================================
  // The app marks every line of a box with "_bogos_trigger": BP_<offer>__<box id>_<step>.
  // The gift goes onto the box's first-step line as visible properties
  // (Gift From / Gift To / Gift Message), so it shows in the cart, at
  // checkout, on the order and on the packing slip, one per box.
  // Several boxes can be waiting at once (a fast shopper starts the next box
  // before the last one reached the cart), so pending gifts are kept as a list.
  // A gift stays in the list (with the box it went to, g.box) until the cart
  // shows it on that box: right after the box lands, a write can be undone by
  // other cart updates on the page, and then it is written again (max MAX_TRIES).
  const PENDING_KEY = 'lcPendingGift';
  const MAX_TRIES = 4;
  const PENDING_MAX_AGE = 30 * 60 * 1000;

  function validGift(g) {
    return g && typeof g.t === 'number' && Date.now() - g.t < PENDING_MAX_AGE &&
      ['from', 'to', 'message', 'offer'].every(k => typeof g[k] === 'string');
  }
  function getPendingGifts() {
    try {
      let list = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
      if (!Array.isArray(list)) list = [list];
      const ok = list.filter(validGift);
      if (ok.length !== list.length) savePendingGifts(ok);
      return ok;
    } catch (e) { /* ignore */ }
    return [];
  }
  function savePendingGifts(list) {
    try {
      if (list.length) localStorage.setItem(PENDING_KEY, JSON.stringify(list));
      else localStorage.removeItem(PENDING_KEY);
    } catch (e) { /* ignore */ }
  }
  function setPendingGift(g) {
    const list = getPendingGifts();
    list.push(g);
    savePendingGifts(list);
  }

  function boxInfo(item) {
    const m = /^BP_(\d+)__(.+)_(\d+)$/.exec((item.properties && item.properties._bogos_trigger) || '');
    return m ? { offer: m[1], box: m[2], step: parseInt(m[3], 10) } : null;
  }

  let assigning = false;
  function assignPendingGift() {
    const pending = getPendingGifts();
    if (!pending.length || assigning) return;
    assigning = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        // Boxes without a gift yet, newest first (new lines go to the top of the
        // cart), each represented by its lowest-step line.
        const boxes = {};
        const order = [];
        cart.items.forEach(item => {
          const b = boxInfo(item);
          if (!b) return;
          if (!boxes[b.box]) { boxes[b.box] = null; order.push(b.box); }
          const cur = boxes[b.box];
          if (!cur || b.step < boxInfo(cur).step) boxes[b.box] = item;
        });
        const jobs = [];
        const keep = [];
        const claimed = {};
        // Gifts already written to a box: done when the box shows it, written
        // again when it went missing, dropped when the box left the cart.
        pending.filter(g => g.box).forEach(g => {
          const it = boxes[g.box];
          if (!it) return;
          claimed[g.box] = true;
          if (plain(it.properties['Gift From']) === plain(g.from) && plain(it.properties['Gift To']) === plain(g.to)) return;
          if ((g.tries || 0) >= MAX_TRIES) return;
          keep.push(g);
          jobs.push({ g: g, item: it });
        });
        const open = order.filter(id => !claimed[id]).map(id => boxes[id]).filter(it => it && !it.properties['Gift From']);
        // Newest new gift goes to the newest open box of its builder, and so on.
        pending.filter(g => !g.box).sort((x, y) => y.t - x.t).forEach(g => {
          keep.push(g);
          const i = open.findIndex(it => !g.offer || boxInfo(it).offer === g.offer);
          if (i < 0) return;
          jobs.push({ g: g, item: open.splice(i, 1)[0] });
        });
        // Keep gifts still waiting for a box or for their write to show; drop
        // gifts confirmed on their box. Gifts saved meanwhile (other tab) stay.
        const seen = pending.map(g => g.t);
        savePendingGifts(keep.concat(getPendingGifts().filter(x => seen.indexOf(x.t) < 0)));
        if (!jobs.length) return null;
        let done = 0;
        return jobs.reduce((p, job) => p.then(() => {
          const props = Object.assign({}, job.item.properties, {
            'Gift From': job.g.from, 'Gift To': job.g.to, 'Gift Message': job.g.message
          });
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: job.item.key, quantity: job.item.quantity, properties: props })
          }).then(r => {
            if (!r.ok) return;
            done++;
            const box = boxInfo(job.item).box;
            savePendingGifts(getPendingGifts().map(x => x.t === job.g.t ?
              Object.assign({}, x, { box: box, tries: (x.tries || 0) + 1 }) : x));
          });
        }), Promise.resolve()).then(() => {
          if (!done) return;
          // Check the write held; the next run writes it again if it didn't.
          setTimeout(assignPendingGift, 3000);
          return syncBoxSummary().then(() => {
            // The cart page was drawn before the message was saved: show it.
            if (/^\/cart\/?$/.test(window.location.pathname)) window.location.reload();
          });
        });
      })
      .catch(console.error)
      .then(() => { assigning = false; });
  }

  // Any page: finish gifts that were waiting for their box to reach the cart.
  // The box can still be on its way when the next page opens, so try again shortly.
  if (getPendingGifts().length) {
    const run = () => { assignPendingGift(); [3000, 8000].forEach(ms => setTimeout(assignPendingGift, ms)); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
    else run();
  }

  // ============================================
  // BOX SUMMARY ON THE ORDER
  // ============================================
  // The order lists box items as separate lines, so the packer can't tell which
  // items go in which box. Keep a cart attribute "Gift boxes" listing each box
  // with its items and gift message; it shows on the order under "Additional details".
  const SUMMARY_KEY = 'Gift boxes';
  const BUILDER_NAMES = { '32529': 'Kids', '20318': 'Baby' };

  function plain(text) {
    const d = document.createElement('textarea');
    d.innerHTML = text || '';
    return d.value;
  }

  function boxSummary(cart) {
    const boxes = {};
    const order = [];
    const loose = [];
    cart.items.forEach(item => {
      const b = boxInfo(item);
      if (!b) { loose.push(item); return; }
      if (!boxes[b.box]) { boxes[b.box] = { offer: b.offer, lines: [] }; order.push(b.box); }
      boxes[b.box].lines.push({ step: b.step, item: item });
    });
    // New lines go to the top of the cart, so the oldest box is last.
    order.reverse();
    if (!order.length) return '';
    const notInBox = loose.length ? '\n\nNot in a box:\n' + loose.slice().reverse().map(it =>
      '  - ' + plain(it.title) + (it.quantity > 1 ? ' x' + it.quantity : '')).join('\n') : '';
    return order.map((id, i) => {
      const box = boxes[id];
      box.lines.sort((a, b) => a.step - b.step);
      const first = box.lines[0].item;
      const name = BUILDER_NAMES[box.offer] ? ' (' + BUILDER_NAMES[box.offer] + ')' : '';
      const out = ['Box ' + (i + 1) + name + ': ' + plain(first.title) + (first.quantity > 1 ? ' x' + first.quantity : '')];
      box.lines.slice(1).forEach(l => {
        out.push('  - ' + plain(l.item.title) + (l.item.quantity > 1 ? ' x' + l.item.quantity : ''));
      });
      const gift = box.lines.map(l => l.item.properties || {}).find(p => p['Gift From']);
      if (gift) {
        out.push('  Gift From: ' + gift['Gift From'] + ' / To: ' + (gift['Gift To'] || '') +
          (gift['Gift Message'] ? ' / Message: ' + gift['Gift Message'] : ''));
      }
      return out.join('\n');
    }).join('\n\n') + notInBox;
  }

  let summarySyncing = null;
  function syncBoxSummary() {
    if (summarySyncing) return summarySyncing;
    summarySyncing = fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        const text = boxSummary(cart);
        const cur = (cart.attributes && cart.attributes[SUMMARY_KEY]) || '';
        if (text === cur) return;
        const attributes = {};
        attributes[SUMMARY_KEY] = text;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attributes })
        });
      })
      .catch(console.error)
      .then(() => { summarySyncing = null; });
    return summarySyncing;
  }

  // Every page load (covers the cart page after a box is added or removed).
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncBoxSummary);
  else syncBoxSummary();

  // Last check right before checkout, in case the cart changed without a page load.
  let checkoutSynced = false;
  document.addEventListener('click', function(e) {
    const btn = e.target.closest && e.target.closest('[name="checkout"], a[href^="/checkout"]');
    if (!btn || checkoutSynced) return;
    e.preventDefault();
    e.stopPropagation();
    checkoutSynced = true;
    const go = () => btn.click();
    Promise.race([syncBoxSummary(), new Promise(res => setTimeout(res, 2500))]).then(go, go);
  }, true);

  // ============================================
  // VISIBILITY
  // ============================================
  function validate() {
    if (!btnEl || !formEl) return;
    if (formEl.hidden) {
      setDisplay(btnEl, 'none');
      return;
    }
    const fromVal = fromInput.value.trim();
    const toVal = toInput.value.trim();
    const msgVal = msgInput.value.trim();

    let isValid = fromVal && toVal;
    if (CONFIG.requireMessage) isValid = isValid && msgVal;

    setDisplay(btnEl, isValid ? '' : 'none');
  }

  // Write to the DOM only when the value changes. The builder app refreshes
  // itself whenever its markup changes, so repeated identical writes would
  // keep it (and us) re-running in a loop.
  function setDisplay(el, value) {
    if (el.style.display !== value) el.style.display = value;
  }

  function updateVisibility() {
    if (!formEl) return;

    const show = isOnLastStep() || (CONFIG.stayVisibleAfterReveal && revealed);
    const wasHidden = formEl.hidden;
    if (formEl.hidden !== !show) formEl.hidden = !show;
    validate();

    if (show && !revealed) {
      revealed = true;
      if (CONFIG.scrollOnReveal && wasHidden) scrollToForm();
    }
  }

  // On phones the app shows "Your bundle" as a fixed bar; class "active" means it is open.
  function openPanelOnPhone() {
    if (!CONFIG.openPanelOnPhone || !formEl || formEl.hidden) return false;
    const panel = formEl.closest('.bogos-bp-widget');
    if (!panel || panel.classList.contains('active')) return false;
    if (window.innerWidth >= 600 || getComputedStyle(panel).position !== 'fixed') return false;
    const toggle = panel.querySelector('.bogos-bp-widget-toggle');
    if (!toggle) return false;
    toggle.click();
    return true;
  }

  let scrollPending = false;
  function scrollToForm() {
    if (!formEl || formEl.hidden || scrollPending) return;
    scrollPending = true;
    // Give BOGOS a moment to finish updating the UI, then slide down.
    setTimeout(() => {
      scrollPending = false;
      if (!formEl || formEl.hidden) return;
      formEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (fromInput) setTimeout(() => fromInput.focus({ preventScroll: true }), 700);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
